import type { CSSProperties, Dispatch, SetStateAction } from 'react';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';
import type { Profile, TranslateFn } from '../../types';
import type { NavTab } from './renderBottomNav';
import { themeModeIcon, themeModeLabel, themeButtonTitle, type ThemeMode } from '../../lib/theme';
import { rollenLabelKey, rollenIcon } from '../../lib/zugang.js';

interface RenderBottomSheetProps {
  user: Profile;
  themeMode: ThemeMode;
  cycleThemeMode: () => void;
  lang: string;
  setLang: (l: string) => void;
  langOpen: boolean;
  setLangOpen: Dispatch<SetStateAction<boolean>>;
  LANGS: readonly string[];
  LANG_LABELS: Record<string, string>;
  LANG_FLAGS: Record<string, string>;
  /** Aktive Tab-ID. */
  tab: string;
  nav: (tabId: string) => void;
  navTabs: NavTab[];
  /** Ungelesen-Zähler pro Tab-ID. */
  navBadges: Record<string, number>;
  logout: () => void;
  setBottomSheet: Dispatch<SetStateAction<boolean>>;
  t: TranslateFn;
}

/**
 * renderBottomSheet — Bottom-Sheet-Overlay mit erweiterten Navigations-Optionen
 * (nur Mobile).
 *
 * Zeigt alle Nav-Tabs in einem 3-Spalten-Grid, einen Dark-/Lang-Toggle-Paar
 * und eine User-Card mit Abmelde-Button. Wird vom "Mehr"-Button in
 * `renderBottomNav` gesteuert.
 */
export function renderBottomSheet({
  user,
  themeMode,
  cycleThemeMode,
  lang,
  setLang,
  langOpen,
  setLangOpen,
  LANGS,
  LANG_LABELS,
  LANG_FLAGS,
  tab,
  nav,
  navTabs,
  navBadges,
  logout,
  setBottomSheet,
  t,
}: RenderBottomSheetProps) {
  return (
    <div
      style={{
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 201,
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        borderRadius: '20px 20px 0 0',
        padding: '12px 16px env(safe-area-inset-bottom, 16px)',
        boxShadow: '0 -8px 40px rgba(0,0,0,.2)',
        animation: 'sheetUp .25s cubic-bezier(.2,0,.1,1)',
        maxHeight: '70vh',
        overflowY: 'auto' as const,
      }}>
      <div
        style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />
      {/* Grid of all nav items */}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
        {navTabs
          .filter(function (nt) {
            return !nt.divider;
          })
          .map(function (nt) {
            return (
              <button
                key={nt.id}
                onClick={function () {
                  nav(nt.id);
                  setBottomSheet(false);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  gap: 6,
                  padding: '14px 8px',
                  border: '1px solid ' + (tab === nt.id ? 'var(--accent)' : 'var(--border)'),
                  background: tab === nt.id ? 'rgba(74,222,128,.08)' : 'var(--surface2)',
                  borderRadius: 14,
                  cursor: 'pointer',
                  color: tab === nt.id ? 'var(--accent)' : 'var(--text)',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 11,
                  fontWeight: tab === nt.id ? 700 : 500,
                  transition: 'all .15s',
                  position: 'relative' as const,
                }}>
                {navBadges[nt.id] > 0 &&
                  <span
                    style={{
                      position: 'absolute' as const,
                      top: 4,
                      right: 4,
                      background: nt.id === 'admin-users' ? '#f59e0b' : '#ef4444',
                      color: '#fff',
                      borderRadius: 20,
                      padding: '0 5px',
                      fontSize: 8,
                      fontWeight: 700,
                      minWidth: 14,
                      textAlign: 'center' as const,
                      lineHeight: '14px',
                    }}>
                    {navBadges[nt.id] > 9 ? '9+' : navBadges[nt.id]}
                  </span>}
                <Icon name={nt.icon} size={22} />
                <span>
                  {nt.label}
                </span>
              </button>
            );
          })}
      </div>
      {/* Dark/Lang toggle */}
      <div style={{ ...(S.row as CSSProperties), gap: 8, marginBottom: 12 }}>
        <button
          onClick={cycleThemeMode}
          title={themeButtonTitle(themeMode, t)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '12px',
            fontSize: 13,
            cursor: 'pointer',
            color: 'var(--text)',
            fontFamily: "'DM Sans',sans-serif",
          }}>
          <Icon name={themeModeIcon(themeMode)} size={16} color={themeMode === 'light' ? undefined : 'var(--text)'} />
          {themeModeLabel(themeMode, t)}
        </button>
        <button
          onClick={function () {
            setLangOpen(function (o) {
              return !o;
            });
          }}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: '12px',
            fontSize: 13,
            cursor: 'pointer',
            color: 'var(--text)',
            fontFamily: "'DM Sans',sans-serif",
          }}>
          {LANG_FLAGS[lang]}
          {' '}
          {LANG_LABELS[lang]}
        </button>
      </div>
      {/* Lang selector */}
      {langOpen &&
        <div style={{ ...(S.rowWrap as CSSProperties), gap: 6, marginBottom: 12 }}>
          {LANGS.map(function (l) {
            return (
              <button
                key={l}
                onClick={function () {
                  setLang(l);
                  setLangOpen(false);
                }}
                style={{
                  padding: '10px 14px',
                  border: '1px solid ' + (l === lang ? 'var(--accent)' : 'var(--border)'),
                  background: l === lang ? 'rgba(74,222,128,.1)' : 'var(--surface2)',
                  borderRadius: 10,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: 'var(--text)',
                  fontWeight: l === lang ? 700 : 400,
                }}>
                {LANG_FLAGS[l]}
                {' '}
                {LANG_LABELS[l]}
              </button>
            );
          })}
        </div>}
      {/* User card + Logout */}
      <div
        style={{
          ...(S.rowBetween as CSSProperties),
          padding: '12px 14px',
          background: 'var(--surface2)',
          borderRadius: 12,
        }}>
        <div style={{ ...(S.row as CSSProperties), gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(34,197,94,.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
            }}>
            <Icon
              name={rollenIcon(user)}
              size={16} />
          </div>
          <div>
            <div style={S.subhead as CSSProperties}>
              {user.firma || user.name}
            </div>
            <div className="t-muted-xs">
              {rollenLabelKey(user) ? t(rollenLabelKey(user) as string) : 'Admin'}
            </div>
          </div>
        </div>
        <button
          onClick={function () {
            logout();
            setBottomSheet(false);
          }}
          style={{
            background: 'rgba(239,68,68,.08)',
            border: '1px solid rgba(239,68,68,.2)',
            borderRadius: 10,
            padding: '8px 14px',
            fontSize: 12,
            cursor: 'pointer',
            color: 'var(--danger)',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
          <Icon name="key" size={12} color="var(--danger)" />
          {t('abmelden')}
        </button>
      </div>
    </div>
  );
}
